import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NavigationCancel, NavigationError, Router } from '@angular/router';
import { catchError, filter, of, Subject, takeUntil } from 'rxjs';
import { emailValidator } from '../../../validators/email/email.validator';
import { DataService } from '../../../services/data/data.service';
import { IApiClaimsHistory, IContract, IContractData } from '../../../interfaces/IJob';
import { phoneValidator } from '../../../validators/phone/phone.validator';
import { ContactService } from '../../../services/contact/contact.service';
import { JobService } from '../../../services/job/job.service';
import { IClaimQuestionForm, IClaimQuestionResponse } from '../../../interfaces/IContactUs';
import { CONTACT_REASON_OPTIONS, CONTACT_REASONS, MAX_QUERY_LENGTH } from '../../../utils/constants';
import { RouterService } from '../../../services/router/router.service';
import { APP_ROUTES } from '../../../utils/route.constant';
import { CONTACT_US_HELP_BUTTON_NAME, CONTACT_US_PAGE, ERR_EVENT, GO_BACK_TO_DASHBOARD, SUBMIT_CONTACT_CINCH_QUERY, SUBMIT_THE_QUERY_EXISTING_JOB, SUBMIT_THE_QUERY_NEW_JOB, SUBMIT_THE_QUERY_PLAN_BILLING, SUBMIT_THE_QUERY_PLAN_COVERAGE } from '../../../services/ga4/ga4.constant';
import { UtilitiesService } from '../../../services/utilities/utilities.service';
import { LiferayService } from '../../../services/liferay/liferay.service';
import { LIFERAY_WEB_CONSTANT } from '../../../utils/liferay.constant';
import { IContactUSContent } from '../../../services/liferay/liferay.interface';
import { Ga4Service } from '../../../services/ga4/ga4.service';

@Component({
  selector: 'app-contact-cinch',
  templateUrl: './contact-cinch.component.html',
  styleUrl: './contact-cinch.component.scss'
})
export class ContactCinchComponent implements OnInit, OnDestroy {

  contactForm!: FormGroup;
  cleaner$: Subject<void> = new Subject<void>();
  baseContractData!: IContract;
  customerServicePhoneNumber: string = '';
  characterLength: string = MAX_QUERY_LENGTH;
  isSubmitForm: boolean = false;
  showConfirmation: boolean = false;
  errorMsg: string = '';
  contactUsPageLocation: string = CONTACT_US_PAGE;
  helpPhoneNumberName: string = CONTACT_US_HELP_BUTTON_NAME;
  entryPageContent: string = ''
  confirmationPageContent: string = '';
  showExistingJobSelect: boolean = false;
  jobOptions: { label: string; value: string }[] = [];
  isNavigatingToServiceRequest: boolean = false;
  prefilledJobId: string | null = null;
  reasonOptions = CONTACT_REASON_OPTIONS;
  buttonName: string = SUBMIT_CONTACT_CINCH_QUERY;

  constructor(
    private dataService: DataService,
    private contactService: ContactService,
    private routerService: RouterService,
    private fb: FormBuilder,
    private utilitiesService: UtilitiesService,
    private lifeRayService: LiferayService,
    private ga4Service: Ga4Service,
    private jobService: JobService,
    private router?: Router
  ) {

  }


  /**
   * @description Initialize component
   */
  ngOnInit(): void {
    this.initializeContactForm();
    this.checkPrefillData();
    this.getContractData();
    this.getContactUsPageContent();
    this.listenToRouterEvents();
  }

  private checkPrefillData(): void {
    const prefillData = this.dataService.contactUsPrefill$?.getValue();
    if (!prefillData) {
      return;
    }

    this.dataService.contactUsPrefill$.next(null);

    const { reason, serviceOrderId } = prefillData;
    if (serviceOrderId) {
      this.prefilledJobId = serviceOrderId;
    }

    const isValidReason = CONTACT_REASON_OPTIONS.some(option => option.value === reason);
    if (reason && isValidReason) {
      this.contactForm.get('reasonForContact')?.setValue(reason);
      this.onReasonChange(reason, false);
    }
  }

  private listenToRouterEvents(): void {
    if (!this.router) {
      return;
    }
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationCancel || event instanceof NavigationError),
        takeUntil(this.cleaner$)
      )
      .subscribe(() => {
        this.isNavigatingToServiceRequest = false;
        this.contactForm?.enable();
        this.contactForm?.get('reasonForContact')?.setValue(null);
        this.showExistingJobSelect = false;
        this.contactForm?.get('jobDetails')?.clearValidators();
        this.contactForm?.get('jobDetails')?.setValue(null);
        this.contactForm?.get('jobDetails')?.updateValueAndValidity();
      });
  }

  getContactUsPageContent(): void {
    let content = LIFERAY_WEB_CONSTANT.contact_us_content;
    this.lifeRayService
      .getWebContent<IContactUSContent>(content.type, content.id)
      .pipe(
        takeUntil(this.cleaner$),
        catchError(() => of({ value: null as IContactUSContent | null }))
      )
      .subscribe(({ value }) => {
        const contentToUse = value?.normalContent?.display ? value.normalContent : value?.extendedContent;
        if (!contentToUse) {
          this.entryPageContent = '';
          this.confirmationPageContent = '';
          return;
        }
        this.entryPageContent = contentToUse.forEntrypage ?? '';
        this.confirmationPageContent = contentToUse.forConfirmationPage ?? '';
        this.applyPhoneNumberAnchor();
      });
  }

  private applyPhoneNumberAnchor(): void {
    if (!this.customerServicePhoneNumber) {
      return;
    }
    const anchorCustomerServicePhoneNumber =
      this.utilitiesService.formatPhoneNumberinStringasAnchortag(this.customerServicePhoneNumber, false);
    this.entryPageContent = this.entryPageContent.replace('[phone number]', anchorCustomerServicePhoneNumber);
    this.confirmationPageContent = this.confirmationPageContent.replace('[phone number]', anchorCustomerServicePhoneNumber);
  }

  /**
   * @description Get contract data
   */
  getContractData(): void {
    this.dataService.contractData$.pipe(takeUntil(this.cleaner$)).subscribe({
      next: (contractData: IContractData | null) => {
        if (!contractData || !contractData?.baseContract) {
          this.contactForm.reset();
          this.routerService.goto(APP_ROUTES.DASHBOARD);
          return;
        }

        const { baseContract } = contractData;
        const { primaryCustomer, primaryCustomerEmail, primaryCustomerPhone } = baseContract;

        this.baseContractData = baseContract;

        this.contactForm.patchValue({
          firstName: primaryCustomer.firstName,
          lastName: primaryCustomer.lastName,
          email: primaryCustomerEmail,
          phone: primaryCustomerPhone.phoneNumber,
        });
        this.contactForm.markAllAsTouched();
        this.customerServicePhoneNumber = this.utilitiesService.getContactNumber(baseContract);
        this.applyPhoneNumberAnchor();
        this.loadJobs(baseContract.contractNumber);
      },
    });
  }

  /**
   * @description Load jobs for the contract and filter to past 180 days
   */
  loadJobs(contractNumber: string): void {
    const cachedJobs = this.dataService.jobData$.getValue();
    if (cachedJobs && cachedJobs.length > 0) {
      this.processJobs(cachedJobs);
    }
    this.jobService
      .getJobsData(contractNumber)
      .pipe(takeUntil(this.cleaner$))
      .subscribe({
        next: (jobs: IApiClaimsHistory[]) => {
          this.processJobs(jobs);
        },
        error: () => {
          if (!this.jobOptions.length) {
            const cached = this.dataService.jobData$.getValue();
            if (cached && cached.length > 0) {
              this.processJobs(cached);
            }
          }
        }
      });
  }

  /**
   * @description Process jobs list, filter up to 180 days in past, and map to display format
   */
  processJobs(jobs: IApiClaimsHistory[]): void {
    if (!jobs || !jobs.length) {
      this.jobOptions = [];
    } else {
      const filteredJobs = jobs.filter(job => {
        if (this.isPrefilledJob(job)) {
          return true;
        }
        const jobTime = this.utilitiesService.getJobTimestamp(job);
        return this.utilitiesService.isJobWithin180Days(jobTime);
      });

      this.jobOptions = filteredJobs.map(job => {
        const itemName = job.models?.[0]?.productLineDescription ||
          job.models?.[0]?.itemName ||
          job.serviceDescription ||
          'Service Request';
        const srNumber = job.serviceOrderId ? `#${job.serviceOrderId}` : '';
        const display = srNumber ? `${itemName} ${srNumber}` : itemName;
        return {
          label: display,
          value: display
        };
      });
    }

    const jobDetailsControl = this.contactForm?.get('jobDetails');
    if (jobDetailsControl?.value && !this.jobOptions.some(opt => opt.value === jobDetailsControl.value)) {
      jobDetailsControl.setValue(null);
      jobDetailsControl.updateValueAndValidity();
    }

    if (this.showExistingJobSelect) {
      if (this.prefilledJobId) {
        const matchedOption = this.jobOptions.find(opt =>
          opt.value.toLowerCase().includes(this.prefilledJobId!.toLowerCase()) ||
          opt.label.toLowerCase().includes(this.prefilledJobId!.toLowerCase())
        );
        if (matchedOption) {
          this.contactForm.get('jobDetails')?.setValue(matchedOption.value);
        }
      } else if (this.jobOptions.length === 1 && !this.contactForm.get('jobDetails')?.value) {
        this.contactForm.get('jobDetails')?.setValue(this.jobOptions[0].value);
      }
    }
  }

  private isPrefilledJob(job: IApiClaimsHistory): boolean {
    if (!this.prefilledJobId) {
      return false;
    }
    const cleanPrefilled = this.prefilledJobId.replace(/^#/, '').trim().toLowerCase();
    return (
      job.serviceOrderId?.toLowerCase() === cleanPrefilled ||
      job.serviceJobId?.toLowerCase() === cleanPrefilled
    );
  }

  /**
   * @description Handle reason selection
   */
  onReasonChange(selectedReason: string, isUserAction: boolean = true): void {
    this.setSubmitQueryGa4ButtonName(selectedReason);
    if (selectedReason === CONTACT_REASONS.NEW_SERVICE_JOB) {
      this.isNavigatingToServiceRequest = true;
      this.contactForm.disable();
      if (isUserAction) {
        this.ga4Service.buttonClickEvent(CONTACT_US_PAGE, this.buttonName);
      }
      this.routerService.goto(APP_ROUTES.SERVICE_REQUEST);
      return;
    }

    this.isNavigatingToServiceRequest = false;
    if (selectedReason === CONTACT_REASONS.EXISTING_SERVICE_JOB) {
      this.showExistingJobSelect = true;
      this.contactForm.get('jobDetails')?.setValidators([Validators.required]);
      if (this.prefilledJobId && !this.contactForm.get('jobDetails')?.value) {
        const matchedOption = this.jobOptions.find(opt =>
          opt.value.toLowerCase().includes(this.prefilledJobId!.toLowerCase()) ||
          opt.label.toLowerCase().includes(this.prefilledJobId!.toLowerCase())
        );
        if (matchedOption) {
          this.contactForm.get('jobDetails')?.setValue(matchedOption.value);
        }
      }
      if (this.jobOptions.length === 1 && !this.contactForm.get('jobDetails')?.value) {
        this.contactForm.get('jobDetails')?.setValue(this.jobOptions[0].value);
      }
      this.contactForm.get('jobDetails')?.updateValueAndValidity();
    } else {
      this.showExistingJobSelect = false;
      this.contactForm.get('jobDetails')?.clearValidators();
      this.contactForm.get('jobDetails')?.setValue(null);
      this.contactForm.get('jobDetails')?.updateValueAndValidity();
    }
  }

  /**
   * @description Initialize contact form
   */
  initializeContactForm(): void {
    this.contactForm = this.fb.group({
      'reasonForContact': [null, [Validators.required]],
      'jobDetails': [null],
      'phone': [null, [phoneValidator(), Validators.required]],
      'firstName': [null, [Validators.minLength(2), Validators.required, Validators.maxLength(20)]],
      'lastName': [null, [Validators.minLength(2), Validators.required, Validators.maxLength(20)]],
      'email': [null, [Validators.required, emailValidator()]],
      'question': [null, [Validators.minLength(2), Validators.required, Validators.maxLength(240)]],
    });
  }

  setSubmitQueryGa4ButtonName(selectedReason: string): string {
    switch (selectedReason) {
      case CONTACT_REASONS.EXISTING_SERVICE_JOB:
        this.buttonName = SUBMIT_THE_QUERY_EXISTING_JOB;
        break;
      case CONTACT_REASONS.NEW_SERVICE_JOB:
        this.buttonName = SUBMIT_THE_QUERY_NEW_JOB;
        break;
      case CONTACT_REASONS.PLAN_BILLING:
        this.buttonName = SUBMIT_THE_QUERY_PLAN_BILLING;
        break;
      case CONTACT_REASONS.PLAN_COVERAGE:
        this.buttonName = SUBMIT_THE_QUERY_PLAN_COVERAGE;
        break;
      default:
        this.buttonName = SUBMIT_CONTACT_CINCH_QUERY;
        break;
    }
    return this.buttonName;
  }

  /**
   * @description Submit contact form
   * @param contactForm 
   */
  onSubmit(contactForm: FormGroup): void {
    this.isSubmitForm = true;
    if (this.contactForm.valid) {
      const { propertyAddress, contractNumber } = this.baseContractData;
      const formVal = contactForm.value;
      const payload: IClaimQuestionForm = {
        ...formVal,
        city: propertyAddress.city,
        state: propertyAddress.state,
        zip: propertyAddress.zip,
        contractNumber: contractNumber,
        propertyAddress: propertyAddress.addressLine1,
        reasonForContact: formVal.reasonForContact,
        jobDetails: this.showExistingJobSelect ? formVal.jobDetails : null,
      };
      const buttonName = this.setSubmitQueryGa4ButtonName(formVal.reasonForContact);
      this.ga4Service.buttonClickEvent(CONTACT_US_PAGE, buttonName);
      this.contactService.submitContactForm(payload).pipe(takeUntil(this.cleaner$)).subscribe({
        next: (data: IClaimQuestionResponse) => {
          if (data.successFlag == 'Y') {
            this.isSubmitForm = false;
            this.showConfirmation = true;
          }
        },
        error: e => {
          this.isSubmitForm = false;
          this.showConfirmation = false;
          this.errorMsg = e.error.message
          this.ga4Service.messageEvent(ERR_EVENT, this.errorMsg);
        }
      });
    }
  }

  /**
   * @description Calculate character length
   */
  questionCharacterlengthcalculation(): void {
    this.characterLength = (240 - this.contactForm.controls['question'].value.length).toString();
  }

  /**
   * @description Go to dashboard
   */
  goToDashboard(): void {
    this.ga4Service.buttonClickEvent(CONTACT_US_PAGE, GO_BACK_TO_DASHBOARD);
    this.routerService.goto(APP_ROUTES.DASHBOARD);
  }

  /**
   * @description Destroy component
   */
  ngOnDestroy(): void {
    this.cleaner$.next();
    this.cleaner$.complete();
  }
}
